import { FC, useState } from 'react';

import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import { fmtTime } from './couponDetail/constants';
import { CrossOrderHashOverlapResult } from './couponDetail/kakaoShareOverlap';

const chatTypeLabel: Record<string, string> = {
  MemoChat: '나에게 보내기',
  DirectChat: '1:1 채팅',
  MultiChat: '단톡방',
  OpenMultiChat: '오픈 단톡방',
};

interface CrossOrderHashOverlapBannerProps {
  overlap: CrossOrderHashOverlapResult;
  /** orderId → order (해당 주문 행으로 점프하기 위해 필요) */
  orderMap: Map<string, GiftOrderForAdmin>;
  /** 콜백 chip 클릭 → 해당 주문 행으로 스크롤 (옵션) */
  onSelectOrder?: (orderId: string) => void;
}

/**
 * "다건수신 (주문간)" 위험 배너.
 *
 * - 정의: 검색 결과 안에서 서로 다른 주문의 쿠폰이 동일한 카카오톡 채팅방
 *   (`hashChatId`)으로 공유된 케이스 = 한 카톡방이 여러 주문의 쿠폰을 함께 수신.
 * - 의미: 중복/대리 발송 의심.
 */
export const CrossOrderHashOverlapBanner: FC<CrossOrderHashOverlapBannerProps> = ({
  overlap,
  orderMap,
  onSelectOrder,
}) => {
  const [expanded, setExpanded] = useState(true);

  if (overlap.groups.length === 0) return null;

  const totalOrders = overlap.overlapOrderIds.size;
  const totalGroups = overlap.groups.length;

  return (
    <div className="mb-4 rounded-lg border border-red-300 bg-red-50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-t-lg px-4 py-2 text-left hover:bg-red-100/70"
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
            ⚠ 다건수신 (주문간)
          </span>
          <p className="text-xs font-semibold text-red-800">
            서로 다른 {totalOrders}개 주문의 쿠폰이 같은 카톡방으로 수신됨
            <span className="ml-1 font-normal text-red-600">
              · {totalGroups}개 채팅방
            </span>
          </p>
        </div>
        <svg
          className={`h-3.5 w-3.5 text-red-700 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {expanded && (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto rounded-b-lg border-t border-red-200 px-4 py-3">
          {overlap.groups.map((g, idx) => {
            const typeLabel = chatTypeLabel[g.chatType] ?? g.chatType ?? '알 수 없음';
            return (
              <div
                key={g.key}
                className="rounded-md border border-red-200 bg-white px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="text-[10px] font-semibold text-red-500">
                    #{idx + 1}
                  </span>
                  <span className="inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                    {g.orderIds.length}개 주문
                  </span>
                  <span className="inline-flex rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 ring-1 ring-inset ring-red-200">
                    {g.couponIds.length}개 쿠폰
                  </span>
                  <span className="inline-flex rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                    {typeLabel}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    카톡방 hashId{' '}
                    <span
                      className="font-mono text-gray-600"
                      title={g.hashChatId}
                    >
                      {g.hashChatId}
                    </span>
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {g.callbacks.map((cb) => {
                    const order = orderMap.get(cb.orderId);
                    const ordererName =
                      order?.bugo?.member?.fullName ||
                      order?.user?.info?.name ||
                      '주문자';
                    return (
                      <button
                        key={cb._id}
                        type="button"
                        onClick={() => onSelectOrder?.(cb.orderId)}
                        title={`${ordererName} · 클릭 시 해당 주문 행으로 이동`}
                        className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100"
                      >
                        <span className="font-mono text-[9px] text-red-500">
                          {cb.orderId.slice(-6)}
                        </span>
                        <span className="text-red-500">#{cb.couponSeq}</span>
                        <span className="font-mono">{cb.couponCode ?? ''}</span>
                        <span className="text-gray-500">
                          {fmtTime(cb.createdAt)}
                        </span>
                        <span className="text-red-400">전달</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
